import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import cloneDeep from 'lodash-es/cloneDeep';
import debounce from 'lodash-es/debounce';
import set from 'lodash-es/set';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ToolkitEditor } from '@/components/organisms/Toolkit/ToolkitEditor/ToolkitEditor';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import { updateRequisite } from '@/redux/nodes/actions';
import { OptionBase } from '@/shared/types/common';
import { Requisite } from '@/shared/types/node';
import { capitalizeFirstLetter, formatErrorMessage } from '@/util/util';
import { LENGTH_XXL } from '@/util/validation/constants';
import { askOnlySchema } from '@/util/validation/schemas/dialog-schemas';

import { QuickOptions } from './QuickOptions';

interface Props {
  requisite: Requisite;
  options: OptionBase[];
  updateErrors: (key: string, error: string | null) => void;
  nodeId: string;
  requisiteIndex: number;
}

type FormType = { ask_only?: string };

const QuestionAskOnly = ({ requisite, updateErrors, nodeId, requisiteIndex }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { variables } = useBrainVariables();

  const {
    formState: { errors },
    trigger,
    setValue,
    control,
  } = useForm<FormType>({
    mode: 'onChange',
    resolver: yupResolver<FormType, unknown, FormType>(askOnlySchema),
    defaultValues: { ask_only: requisite?.actions?.[0]?.texts?.[0] ?? '' },
  });

  // Initialize from Redux
  useEffect(() => {
    const text = requisite?.actions?.[0]?.texts?.[0] ?? '';
    setValue('ask_only', text);
  }, [requisite?.actions, setValue]);

  const handleRequisiteUpdate = useCallback(
    (value: string) => {
      const currentValue = requisite?.actions?.[0]?.texts?.[0] ?? '';
      const currentType = requisite?.actions?.[0]?.type;

      // Bail out if the incoming value matches what we already have
      if (value === currentValue && (!currentType || currentType === 'text')) {
        return;
      }

      const updatedRequisite = cloneDeep(requisite);

      // Clear actions if empty, otherwise update
      if (value === '') {
        set(updatedRequisite, 'actions', undefined);
      } else {
        set(updatedRequisite, 'actions[0].type', 'text');
        set(updatedRequisite, 'actions[0].texts[0]', value);
      }

      dispatch(updateRequisite({ nodeId, index: requisiteIndex, requisite: updatedRequisite }));
    },
    [dispatch, nodeId, requisite, requisiteIndex]
  );

  const debouncedHandleRequisiteUpdate = useMemo(
    () => debounce(handleRequisiteUpdate, 200),
    [handleRequisiteUpdate]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedHandleRequisiteUpdate.cancel();
    };
  }, [debouncedHandleRequisiteUpdate]);

  const handleEditorChange = useCallback(
    (content: string) => {
      setValue('ask_only', content, { shouldValidate: true });
      debouncedHandleRequisiteUpdate(content);
    },
    [setValue, debouncedHandleRequisiteUpdate]
  );

  // Trigger initial validation
  useEffect(() => {
    trigger();
  }, [trigger]);

  // Update dialog errors
  useEffect(() => {
    const errorMessage = errors.ask_only?.message;
    updateErrors(
      t('dialog.question.ask'),
      errorMessage ? capitalizeFirstLetter(errorMessage) : null
    );
  }, [errors.ask_only, t, updateErrors]);

  const currentContent = useWatch({ control, name: 'ask_only' }) ?? '';

  return (
    <>
      <div className="mb-4">
        <label className="mb-2 block text-sm font-medium">{t('dialog.question.ask')}</label>
        <ToolkitEditor
          content={currentContent}
          onContentChange={handleEditorChange}
          variableOptions={variables}
          maxLength={LENGTH_XXL}
        />
        {errors.ask_only && (
          <p className="mt-1 text-sm text-destructive">{formatErrorMessage(errors.ask_only)}</p>
        )}
      </div>
      <QuickOptions requisite={requisite} updateErrors={updateErrors} />
    </>
  );
};

export default QuestionAskOnly;
